import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Search, Filter, Database, Network, Cpu, GitFork } from 'lucide-react';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  PRIMITIVE_METADATA,
  GraphCanvas,
  useGraphDimensions,
  schemaToGraphData,
  domainRegistryToGraphData,
} from '@unwritten/ui-domains';
import type { PrimitiveType } from '../../types/primitives';
import { fetchGraphData } from '../../utils/graphDataTransform';
import { fetchAISystemsGraph, getAISystemsHealthSummary } from '../../utils/aiSystemsGraphTransform';
import type { GraphData } from '../../types/primitives';

type ViewMode = 'data' | 'schema' | 'ai-systems' | 'domain-registry';
type DatabaseMode = 'runtime' | 'design';

interface ExtendedNodeObject {
  id: string;
  type: PrimitiveType;
  label: string;
  properties: Record<string, any>;
  color?: string;
  size?: number;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface ExtendedLinkObject {
  id?: string;
  source: string | ExtendedNodeObject;
  target: string | ExtendedNodeObject;
  type: string;
  label?: string;
  metadata?: Record<string, any>;
}

export function GraphExplorer() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<Set<PrimitiveType>>(
    new Set(Object.keys(PRIMITIVE_METADATA) as PrimitiveType[])
  );
  const [selectedNode, setSelectedNode] = useState<ExtendedNodeObject | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<ExtendedLinkObject | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('data');
  const [databaseMode, setDatabaseMode] = useState<DatabaseMode>('runtime');
  const [rawGraphData, setRawGraphData] = useState<GraphData>({ nodes: [], edges: [] });
  const [isLoading, setIsLoading] = useState(false);

  const fgRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dimensions = useGraphDimensions(containerRef);

  // Fetch data when view mode or database mode changes
  useEffect(() => {
    async function loadData() {
      if (viewMode === 'schema') {
        // Schema mode uses local data
        setRawGraphData(schemaToGraphData());
        return;
      }

      if (viewMode === 'domain-registry') {
        // Domain Registry mode uses local data from domain-registry package
        setRawGraphData(domainRegistryToGraphData());
        return;
      }

      if (viewMode === 'ai-systems') {
        // AI Systems mode fetches agents, flows, and models
        setIsLoading(true);
        try {
          const data = await fetchAISystemsGraph();
          setRawGraphData(data);
        } catch (error) {
          console.error('Failed to load AI systems graph:', error);
          setRawGraphData({ nodes: [], edges: [] });
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Data mode fetches from backend
      setIsLoading(true);
      try {
        const data = await fetchGraphData(databaseMode);
        setRawGraphData(data);
      } catch (error) {
        console.error('Failed to load graph data:', error);
        setRawGraphData({ nodes: [], edges: [] });
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [viewMode, databaseMode]);

  const filteredData = useMemo(() => {
    const nodes = rawGraphData.nodes.filter((node: any) =>
      selectedTypes.has(node.type) &&
      (searchTerm === '' ||
        node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.id.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    const nodeIds = new Set(nodes.map((n: any) => n.id));
    const edges = rawGraphData.edges.filter(
      (edge: any) => nodeIds.has(edge.source) && nodeIds.has(edge.target)
    );

    return { nodes, edges };
  }, [rawGraphData, selectedTypes, searchTerm]);

  useEffect(() => {
    if (fgRef.current) {
      const fg = fgRef.current;
      
      fg.d3Force('charge')?.strength(-400);
      fg.d3Force('link')?.distance(100);
      fg.d3Force('center')?.strength(1);
      
      setTimeout(() => {
        fg.zoomToFit(400, 50);
      }, 500);
    }
  }, [filteredData, viewMode, databaseMode]);

  // Arrange connected nodes around selected node
  useEffect(() => {
    if (fgRef.current && selectedNode) {
      const fg = fgRef.current;
      
      // Get connected node IDs
      const connectedIds = new Set<string>();
      filteredData.edges.forEach((edge: any) => {
        const sourceId = typeof edge.source === 'string' ? edge.source : edge.source.id;
        const targetId = typeof edge.target === 'string' ? edge.target : edge.target.id;
        
        if (sourceId === selectedNode.id) {
          connectedIds.add(targetId);
        }
        if (targetId === selectedNode.id) {
          connectedIds.add(sourceId);
        }
      });
      
      const connectedNodeIds = Array.from(connectedIds);
      
      if (connectedNodeIds.length > 0 && selectedNode.x !== undefined && selectedNode.y !== undefined) {
        // Arrange connected nodes in a circle around the selected node
        const radius = 150; // Distance from center
        const angleStep = (2 * Math.PI) / connectedNodeIds.length;
        
        setTimeout(() => {
          connectedNodeIds.forEach((nodeId, index) => {
            const angle = index * angleStep;
            const targetX = selectedNode.x! + radius * Math.cos(angle);
            const targetY = selectedNode.y! + radius * Math.sin(angle);
            
            // Apply position to node
            const nodeData = fg.graphData().nodes.find((n: any) => n.id === nodeId);
            if (nodeData) {
              nodeData.fx = targetX;
              nodeData.fy = targetY;
            }
          });
          
          // Fix the selected node position
          const selectedNodeData = fg.graphData().nodes.find((n: any) => n.id === selectedNode.id);
          if (selectedNodeData) {
            selectedNodeData.fx = selectedNode.x;
            selectedNodeData.fy = selectedNode.y;
          }
          
          // Reheat the simulation to animate the arrangement
          fg.d3ReheatSimulation();
          
          // Center and zoom to the arrangement
          setTimeout(() => {
            fg.centerAt(selectedNode.x, selectedNode.y, 400);
            fg.zoom(1.5, 400);
          }, 100);
          
          // Release fixed positions after animation
          setTimeout(() => {
            connectedNodeIds.forEach((nodeId) => {
              const nodeData = fg.graphData().nodes.find((n: any) => n.id === nodeId);
              if (nodeData) {
                nodeData.fx = undefined;
                nodeData.fy = undefined;
              }
            });
            if (selectedNodeData) {
              selectedNodeData.fx = undefined;
              selectedNodeData.fy = undefined;
            }
          }, 1500);
        }, 50);
      }
    }
  }, [selectedNode, filteredData]);

  const handleNodeClick = useCallback((node: ExtendedNodeObject) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  const handleLinkClick = useCallback((link: ExtendedLinkObject) => {
    setSelectedEdge(link);
    setSelectedNode(null);
  }, []);
  
  const handleBackgroundClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  const handleExport = () => {
    const dataStr = JSON.stringify(filteredData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `graph-${viewMode}-${databaseMode}.json`;
    link.click();
  };

  const toggleType = (type: PrimitiveType) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rawGraphData.nodes.forEach((node: any) => {
      counts[node.type] = (counts[node.type] || 0) + 1;
    });
    return counts;
  }, [rawGraphData]);

  const healthSummary = useMemo(() => {
    if (viewMode !== 'ai-systems') return null;
    return getAISystemsHealthSummary(rawGraphData);
  }, [viewMode, rawGraphData]);

  const isDesignDBEmpty = viewMode === 'data' && databaseMode === 'design' && rawGraphData.nodes.length === 0 && !isLoading;

  return (
    <div className="h-screen flex flex-col bg-uw-bg-primary">
      <div className="bg-uw-surface-default border-b border-uw-border-default px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/configs"
            className="inline-flex items-center text-uw-interactive-primary hover:text-uw-interactive-primary-hover"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Link>
          <h1 className="text-xl font-bold text-uw-text-primary">Graph Explorer</h1>
          <div className="flex items-center gap-2 text-sm text-uw-text-secondary">
            <div className="w-2 h-2 bg-green-500 rounded-full" />
            <span>Connected</span>
            <span className="ml-2">
              {filteredData.nodes.length} nodes, {filteredData.edges.length} edges
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-uw-surface-elevated rounded p-1">
            <button
              onClick={() => setViewMode('data')}
              className={`px-3 py-1 text-sm rounded flex items-center gap-2 transition-colors ${
                viewMode === 'data'
                  ? 'bg-uw-interactive-primary text-white'
                  : 'text-uw-text-secondary hover:text-uw-text-primary'
              }`}
            >
              <Database className="w-3 h-3" />
              Data
            </button>
            <button
              onClick={() => setViewMode('schema')}
              className={`px-3 py-1 text-sm rounded flex items-center gap-2 transition-colors ${
                viewMode === 'schema'
                  ? 'bg-uw-interactive-primary text-white'
                  : 'text-uw-text-secondary hover:text-uw-text-primary'
              }`}
            >
              <Network className="w-3 h-3" />
              Schema
            </button>
            <button
              onClick={() => setViewMode('domain-registry')}
              className={`px-3 py-1 text-sm rounded flex items-center gap-2 transition-colors ${
                viewMode === 'domain-registry'
                  ? 'bg-uw-interactive-primary text-white'
                  : 'text-uw-text-secondary hover:text-uw-text-primary'
              }`}
            >
              <GitFork className="w-3 h-3" />
              Domain Registry
            </button>
            <button
              onClick={() => setViewMode('ai-systems')}
              className={`px-3 py-1 text-sm rounded flex items-center gap-2 transition-colors ${
                viewMode === 'ai-systems'
                  ? 'bg-uw-interactive-primary text-white'
                  : 'text-uw-text-secondary hover:text-uw-text-primary'
              }`}
            >
              <Cpu className="w-3 h-3" />
              AI Systems
            </button>
          </div>
          {viewMode === 'data' && (
            <div className="flex items-center gap-1 bg-uw-surface-elevated rounded p-1">
              <button
                onClick={() => setDatabaseMode('runtime')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  databaseMode === 'runtime'
                    ? 'bg-uw-interactive-primary text-white'
                    : 'text-uw-text-secondary hover:text-uw-text-primary'
                }`}
              >
                RuntimeDB
              </button>
              <button
                onClick={() => setDatabaseMode('design')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  databaseMode === 'design'
                    ? 'bg-uw-interactive-primary text-white'
                    : 'text-uw-text-secondary hover:text-uw-text-primary'
                }`}
              >
                DesignDB
              </button>
            </div>
          )}
          <button
            onClick={handleExport}
            disabled={isDesignDBEmpty}
            className="px-3 py-2 text-sm bg-uw-surface-elevated hover:bg-uw-surface-elevated/80 text-uw-text-primary rounded flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="w-56 bg-uw-surface-default border-r border-uw-border-default overflow-y-auto flex-shrink-0">
          <div className="p-3 border-b border-uw-border-default">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-uw-text-tertiary" />
              <input
                type="text"
                placeholder="Search nodes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-uw-border-default rounded text-sm bg-uw-bg-secondary text-uw-text-primary placeholder:text-uw-text-tertiary focus:outline-none focus:ring-2 focus:ring-uw-interactive-primary"
              />
            </div>
          </div>

          {viewMode === 'ai-systems' && healthSummary ? (
            <div className="p-3 border-b border-uw-border-default">
              <h3 className="text-xs font-semibold text-uw-text-tertiary uppercase mb-3">
                System Health
              </h3>
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-uw-text-secondary mb-1 font-medium">Agents</div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-uw-text-tertiary">Total</span>
                      <span className="text-uw-text-primary font-medium">{healthSummary.agents.total}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-uw-text-tertiary">Healthy</span>
                      </div>
                      <span className="text-green-500 font-medium">{healthSummary.agents.healthy}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                        <span className="text-uw-text-tertiary">Degraded</span>
                      </div>
                      <span className="text-amber-500 font-medium">{healthSummary.agents.degraded}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        <span className="text-uw-text-tertiary">Down</span>
                      </div>
                      <span className="text-red-500 font-medium">{healthSummary.agents.down}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                        <span className="text-uw-text-tertiary">Idle</span>
                      </div>
                      <span className="text-gray-500 font-medium">{healthSummary.agents.idle}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-uw-text-secondary mb-1 font-medium">Flows</div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-uw-text-tertiary">Total</span>
                      <span className="text-uw-text-primary font-medium">{healthSummary.flows.total}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-uw-text-tertiary">Healthy</span>
                      </div>
                      <span className="text-green-500 font-medium">{healthSummary.flows.healthy}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-uw-text-secondary mb-1 font-medium">Models</div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-uw-text-tertiary">Total</span>
                      <span className="text-uw-text-primary font-medium">{healthSummary.models.total}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                        <span className="text-uw-text-tertiary">Active</span>
                      </div>
                      <span className="text-amber-500 font-medium">{healthSummary.models.active}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-3 border-b border-uw-border-default">
              <h3 className="text-xs font-semibold text-uw-text-tertiary uppercase mb-2 flex items-center gap-2">
                <Filter className="w-3 h-3" />
                Filtering
              </h3>
              <div className="space-y-2">
                {(Object.keys(PRIMITIVE_METADATA) as PrimitiveType[]).map((type) => {
                  const meta = PRIMITIVE_METADATA[type];
                  const Icon = meta.icon;
                  const count = typeCounts[type] || 0;

                  return (
                    <label
                      key={type}
                      className="flex items-center gap-2 cursor-pointer hover:bg-uw-surface-elevated p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTypes.has(type)}
                        onChange={() => toggleType(type)}
                        className="rounded border-uw-border-default"
                      />
                      <Icon className="w-4 h-4" style={{ color: meta.color }} />
                      <span className="text-sm flex-1 text-uw-text-secondary">{meta.name}</span>
                      <span className="text-xs text-uw-text-tertiary">{count}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="p-3">
            <h3 className="text-xs font-semibold text-uw-text-tertiary uppercase mb-2">
              Legend
            </h3>
            <div className="space-y-2">
              {(Object.keys(PRIMITIVE_METADATA) as PrimitiveType[]).map((type) => {
                const meta = PRIMITIVE_METADATA[type];
                const Icon = meta.icon;
                
                return (
                  <div key={type} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: meta.color }}
                    />
                    <Icon className="w-3 h-3" style={{ color: meta.color }} />
                    <span className="text-xs text-uw-text-secondary">{meta.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div ref={containerRef} className="flex-1 min-w-0 relative overflow-hidden">
          <div className="absolute inset-0 bg-uw-surface-default">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-uw-interactive-primary mb-4"></div>
                <p className="text-sm text-uw-text-secondary">Loading graph data...</p>
              </div>
            </div>
          ) : isDesignDBEmpty ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md p-8">
                <Database className="w-16 h-16 mx-auto mb-4 text-uw-text-tertiary" />
                <h3 className="text-lg font-semibold text-uw-text-primary mb-2">
                  DesignDB Not Connected
                </h3>
                <p className="text-sm text-uw-text-secondary mb-4">
                  The Design Database is not available in data view mode. Connect to a DesignDB
                  instance or switch to Schema view to explore the data structure.
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => setViewMode('schema')}
                    className="px-4 py-2 text-sm bg-uw-interactive-primary hover:bg-uw-interactive-primary/90 text-white rounded"
                  >
                    View Schema
                  </button>
                  <button
                    onClick={() => setDatabaseMode('runtime')}
                    className="px-4 py-2 text-sm bg-uw-surface-elevated hover:bg-uw-surface-elevated/80 text-uw-text-primary rounded"
                  >
                    Switch to RuntimeDB
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <GraphCanvas
              ref={fgRef}
              graphData={filteredData}
              width={dimensions.width}
              height={dimensions.height}
              selectedLink={selectedEdge?.id}
              selectedNode={selectedNode?.id || null}
              onNodeClick={handleNodeClick}
              onLinkClick={handleLinkClick}
              onBackgroundClick={handleBackgroundClick}
              enableOffScreenIndicators={true}
              enableLinkLabels={true}
            />
          )}
          </div>
        </div>

        {(selectedNode || selectedEdge) && (
          <div className="w-96 bg-uw-surface-default border-l border-uw-border-default overflow-y-auto p-4 flex-shrink-0">
            {selectedNode && viewMode === 'domain-registry' && (
              <>
                <h3 className="text-lg font-semibold mb-4 text-uw-text-primary">Domain Details</h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">Domain Name</div>
                    <div className="font-medium text-uw-text-primary">{selectedNode.label}</div>
                  </div>
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">Domain ID</div>
                    <code className="text-sm bg-uw-surface-elevated px-2 py-1 rounded text-uw-text-primary">{selectedNode.id}</code>
                  </div>
                  {selectedNode.properties['category'] && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Category</div>
                      <span className="inline-block px-2 py-1 text-xs rounded bg-uw-surface-elevated text-uw-text-primary font-medium">
                        {selectedNode.properties['category']}
                      </span>
                    </div>
                  )}
                  {selectedNode.properties['description'] && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Description</div>
                      <div className="text-sm text-uw-text-secondary">{selectedNode.properties['description']}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">Color</div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded border border-uw-border-default" style={{ backgroundColor: selectedNode.color }} />
                      <code className="text-xs text-uw-text-primary">{selectedNode.color}</code>
                    </div>
                  </div>
                  {selectedNode.properties['tables'] && selectedNode.properties['tables'].length > 0 && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Tables ({selectedNode.properties['tables'].length})</div>
                      <div className="space-y-1">
                        {selectedNode.properties['tables'].map((table: string) => (
                          <code key={table} className="block text-xs bg-uw-surface-elevated px-2 py-1 rounded text-uw-text-primary">
                            {table}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Outgoing</div>
                      <div className="text-lg font-semibold text-uw-text-primary">{selectedNode.properties['outgoingEdges'] || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Incoming</div>
                      <div className="text-lg font-semibold text-uw-text-primary">{selectedNode.properties['incomingEdges'] || 0}</div>
                    </div>
                  </div>
                  {selectedNode.properties['metadata'] && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Status</div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-uw-text-secondary">{selectedNode.properties['metadata'].status || 'unknown'}</span>
                        {selectedNode.properties['metadata'].version && (
                          <span className="text-xs text-uw-text-tertiary">v{selectedNode.properties['metadata'].version}</span>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedNode.properties['parent'] && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Parent Domain</div>
                      <code className="text-sm bg-uw-surface-elevated px-2 py-1 rounded text-uw-text-primary">{selectedNode.properties['parent']}</code>
                    </div>
                  )}
                </div>
              </>
            )}
            {selectedNode && viewMode !== 'domain-registry' && (
              <>
                <h3 className="text-lg font-semibold mb-4 text-uw-text-primary">Node Details</h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">Type</div>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const Icon = PRIMITIVE_METADATA[selectedNode.type].icon;
                        return <Icon className="w-4 h-4" style={{ color: selectedNode.color }} />;
                      })()}
                      <span className="font-medium text-uw-text-primary">{PRIMITIVE_METADATA[selectedNode.type].name}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">ID</div>
                    <code className="text-sm bg-uw-surface-elevated px-2 py-1 rounded text-uw-text-primary">{selectedNode.id}</code>
                  </div>
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">Label</div>
                    <div className="text-sm text-uw-text-secondary">{selectedNode.label}</div>
                  </div>
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">Properties</div>
                    <pre className="text-xs bg-uw-surface-elevated p-2 rounded overflow-x-auto text-uw-text-primary">
                      {JSON.stringify(selectedNode.properties, null, 2)}
                    </pre>
                  </div>
                  <div className="pt-3 border-t border-uw-border-default">
                    <Link
                      to={`/configs/${selectedNode.type}`}
                      className="text-sm text-uw-interactive-primary hover:text-uw-interactive-primary-hover"
                    >
                      View Primitive Details →
                    </Link>
                  </div>
                </div>
              </>
            )}
            {selectedEdge && viewMode === 'domain-registry' && (
              <>
                <h3 className="text-lg font-semibold mb-4 text-uw-text-primary">Edge Details</h3>
                <div className="space-y-3">
                  {selectedEdge.metadata?.['edgeId'] && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Edge ID</div>
                      <code className="text-sm bg-uw-surface-elevated px-2 py-1 rounded text-uw-text-primary block">{selectedEdge.metadata['edgeId']}</code>
                    </div>
                  )}
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">Label</div>
                    <div className="font-medium text-uw-text-primary">{selectedEdge.label || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">From → To</div>
                    <div className="space-y-2">
                      <code className="text-sm bg-uw-surface-elevated px-2 py-1 rounded block text-uw-text-primary">
                        {typeof selectedEdge.source === 'string' ? selectedEdge.source : selectedEdge.source.id}
                      </code>
                      <div className="text-center text-uw-text-tertiary">↓</div>
                      <code className="text-sm bg-uw-surface-elevated px-2 py-1 rounded block text-uw-text-primary">
                        {typeof selectedEdge.target === 'string' ? selectedEdge.target : selectedEdge.target.id}
                      </code>
                    </div>
                  </div>
                  {selectedEdge.metadata?.['relationship'] && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Relationship Type</div>
                      <span className="inline-block px-2 py-1 text-xs rounded bg-uw-surface-elevated text-uw-text-primary font-medium">
                        {selectedEdge.metadata['relationship']}
                      </span>
                    </div>
                  )}
                  {selectedEdge.metadata?.['cardinality'] && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Cardinality</div>
                      <span className="inline-block px-2 py-1 text-xs rounded bg-uw-surface-elevated text-uw-text-primary font-medium">
                        {selectedEdge.metadata['cardinality']}
                      </span>
                    </div>
                  )}
                  {selectedEdge.metadata?.['fieldName'] && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Field Name</div>
                      <code className="text-sm bg-uw-surface-elevated px-2 py-1 rounded text-uw-text-primary">{selectedEdge.metadata['fieldName']}</code>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {selectedEdge.metadata?.['fromTable'] && (
                      <div>
                        <div className="text-xs text-uw-text-tertiary uppercase mb-1">From Table</div>
                        <div className="text-xs text-uw-text-secondary">{selectedEdge.metadata['fromTable']}</div>
                      </div>
                    )}
                    {selectedEdge.metadata?.['toTable'] && (
                      <div>
                        <div className="text-xs text-uw-text-tertiary uppercase mb-1">To Table</div>
                        <div className="text-xs text-uw-text-secondary">{selectedEdge.metadata['toTable']}</div>
                      </div>
                    )}
                  </div>
                  {selectedEdge.metadata?.['bidirectional'] && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Bidirectional</div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span className="text-sm text-uw-text-primary">Yes</span>
                      </div>
                    </div>
                  )}
                  {selectedEdge.metadata && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Visual Properties</div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-uw-text-tertiary">Style:</span>
                          <span className="text-uw-text-primary">{selectedEdge.metadata['style'] || 'solid'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-uw-text-tertiary">Thickness:</span>
                          <span className="text-uw-text-primary">{selectedEdge.metadata['thickness'] || 1}px</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-uw-text-tertiary">Animated:</span>
                          <span className="text-uw-text-primary">{selectedEdge.metadata['animated'] ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            {selectedEdge && viewMode !== 'domain-registry' && (
              <>
                <h3 className="text-lg font-semibold mb-4 text-uw-text-primary">Edge Details</h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">Type</div>
                    <div className="font-medium text-uw-text-primary">{selectedEdge.type}</div>
                  </div>
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">Label</div>
                    <div className="text-sm text-uw-text-secondary">{selectedEdge.label || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">From</div>
                    <code className="text-sm bg-uw-surface-elevated px-2 py-1 rounded block text-uw-text-primary">
                      {typeof selectedEdge.source === 'string' ? selectedEdge.source : selectedEdge.source.id}
                    </code>
                  </div>
                  <div>
                    <div className="text-xs text-uw-text-tertiary uppercase mb-1">To</div>
                    <code className="text-sm bg-uw-surface-elevated px-2 py-1 rounded block text-uw-text-primary">
                      {typeof selectedEdge.target === 'string' ? selectedEdge.target : selectedEdge.target.id}
                    </code>
                  </div>
                  {selectedEdge.metadata && (
                    <div>
                      <div className="text-xs text-uw-text-tertiary uppercase mb-1">Metadata</div>
                      <pre className="text-xs bg-uw-surface-elevated p-2 rounded overflow-x-auto text-uw-text-primary">
                        {JSON.stringify(selectedEdge.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
